import { useState, useEffect } from 'react';
import api from '../services/api';
// config/api.js import 제거 - 이 hook 내에서 실제로 사용되지 않는 미사용 import였음

// [2026-05-19] localStorage 캐시 키 — public/index.html 의 부트스트랩 스크립트와 동일 키.
//   부트스트랩이 React 마운트 전에 캐시를 적용하고 window.__nm_initial_design_template 에 노출하므로,
//   hook 은 그 값을 useState 초기값으로 받아 첫 렌더부터 로고·디자인을 즉시 표시한다.
const CACHE_TTL_MS = 60 * 60 * 1000; // 1시간

const getCachedInitial = () => {
  // 1) 부트스트랩이 이미 노출한 값 우선
  if (typeof window !== 'undefined' && window.__nm_initial_design_template) {
    return window.__nm_initial_design_template;
  }
  // 2) 직접 localStorage 폴백 (부트스트랩이 아직 안 끝난 경우)
  try {
    if (typeof window === 'undefined') return null;
    const key = 'nm_dt_' + window.location.hostname;
    const raw = window.localStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.data && parsed.savedAt && (Date.now() - parsed.savedAt) < CACHE_TTL_MS) {
        return parsed.data;
      }
    }
  } catch (e) { /* 접근 실패는 무시 */ }
  return null;
};

const saveToCache = (data) => {
  try {
    if (typeof window === 'undefined') return;
    const key = 'nm_dt_' + window.location.hostname;
    window.localStorage.setItem(key, JSON.stringify({ data, savedAt: Date.now() }));
  } catch (e) { /* 접근 실패는 무시 */ }
};

// [2026-05-20 Phase 2A] design_tokens JSON 을 :root 의 CSS 변수로 주입.
//   tokens.colors.primary → --color-primary, tokens.colors.background_elevated → --color-background-elevated.
const applyDesignTokens = (tokensRaw) => {
  if (!tokensRaw) return;
  let tokens = tokensRaw;
  if (typeof tokens === 'string') {
    try { tokens = JSON.parse(tokens); } catch (e) { return; }
  }
  if (typeof tokens !== 'object') return;
  const root = document.documentElement;
  const toCssName = (key) => key.replace(/_/g, '-').replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
  Object.entries(tokens).forEach(([category, value]) => {
    if (value === null || value === undefined) return;
    if (typeof value !== 'object') {
      root.style.setProperty(`--${toCssName(category)}`, String(value));
      return;
    }
    Object.entries(value).forEach(([subKey, subValue]) => {
      if (subValue === null || subValue === undefined) return;
      const prefix = (toCssName(category) === 'colors' ? 'color' : (toCssName(category) === 'surfaces' ? 'surface' : toCssName(category)));
      const varName = `--${prefix}-${toCssName(subKey)}`;
      // [2026-06-09] 간격(spacing)·모서리(radius) 토큰은 디자인설정 화면에서 단위 없는 숫자(예: 16)로
      //   입력되므로 padding/border-radius 의 CSS 길이값으로 쓰려면 px 를 부여한다.
      //   이미 단위(px·%·calc 등)가 포함된 값은 그대로 둔다. 다른 카테고리(z_index 등)는 영향 없음.
      let out = String(subValue);
      if ((category === 'spacing' || category === 'radius') && /^-?\d+(\.\d+)?$/.test(out.trim())) {
        out = out.trim() + 'px';
      }
      root.style.setProperty(varName, out);
    });
  });

  // [2026-07-30] '화면 전체 배경' 지정 여부 표시.
  //   WHY: 이 값이 있으면 홈 영역·슬롯/카지노 구역이 '메인 배경'으로 그 위를 덮어 새 배경이
  //        거의 안 보였다(실측: 공지 띠 주변 좁은 틈만 보임). 아래 표시를 근거로 CSS 가 그
  //        덮개만 투명으로 바꾼다. 값이 없으면 표시가 붙지 않아 종전 화면 그대로다(무손상).
  try {
    const appBg = tokens && tokens.colors ? String(tokens.colors.app_background || '').trim() : '';
    if (appBg) document.body.classList.add('has-app-background');
    else document.body.classList.remove('has-app-background');
  } catch (e) { /* 표시 실패는 화면 동작에 영향 없음 */ }
};

// [2026-06-11 Phase 5] 게임사별 고정 랜덤색 도달 경로 — 렌더 지점(ProviderGrid 등)이 random_palette 를 읽도록
//   현재 활성 design_tokens 를 모듈 스코프에 보관하고 getter 로 노출한다(prop drilling/전역 오염 회피).
//   useDomainTheme 의 fetch 성공 시 갱신하고, 부트스트랩(index.html)이 먼저 노출한 window.__nm_initial_design_template
//   을 fallback 으로 사용한다(첫 렌더부터 색 결정 가능, FOUC 방지).
let _activeDesignTokens = null;

const parseTokens = (raw) => {
  if (!raw) return null;
  let t = raw;
  if (typeof t === 'string') {
    try { t = JSON.parse(t); } catch (e) { return null; }
  }
  return (t && typeof t === 'object') ? t : null;
};

export const getActiveDesignTokens = () => {
  if (_activeDesignTokens) return _activeDesignTokens;
  if (typeof window !== 'undefined' && window.__nm_initial_design_template) {
    return parseTokens(window.__nm_initial_design_template.design_tokens);
  }
  return null;
};

// [2026-06-11 Phase 3~5] 게임사 카드 그라데이션 고도화 — 슬롯/카지노 독립 + 멀티스톱·각도·투명도 + 게임사별 고정 랜덤색.
//   WHY: applyDesignTokens 는 변수만 주입(클래스 게이트 없음)하므로, 원본 .provider-item::after 를 한 글자도
//   건드리지 않고 클래스 게이트 뒤 App.css override 로만 그라데이션을 바꾸기 위해 전용 클래스를 부여한다.
//   스키마: gradient.slot = { angle:Number(0~360), stops:[{color,pos}…], random_enabled:Boolean, random_palette:[색…] }, gradient.casino 동일.
//   동작: angle+stops → 완성 linear-gradient 문자열 → CSS.supports 게이트 통과 시에만 --gradient-card-overlay-slot/-casino + has-* 클래스.
//   무손상: 미설정/검증 실패/supports 거부 → 미주입 → 원본 ::after 그대로(디자인6 fallback 은 App.css 의 중첩 var(--surface-modal-overlay,…) 보존).
//   랜덤: :root 완성 문자열 안의 var()는 :root 컨텍스트에서 평가되어 카드별 인라인 색이 무시되므로(실측 확정), 분해 변수
//     (--gradient-card-overlay-{kind}-{angle|pre|pos|fallback-color|post}) + has-gradient-card-overlay-{kind}-random 클래스로
//     전달하고, var(--card-grad-color) 는 App.css 랜덤 전용 ::after 규칙에 직접 둔다(.provider-item 컨텍스트 평가 → 카드별 팔레트색 반영).
//     supports 게이트는 var 없는 완성 문자열(valueProbe) 검사 + 팔레트 각 색 정규식 검증(중대3) 유지.
const GRAD_COLOR_RE = /^(#[0-9a-fA-F]{3,8}|(rgb|rgba|hsl|hsla)\([0-9.,%\s/]+\)|[a-zA-Z]+)$/;

const isValidGradColor = (c) => {
  const s = (c === null || c === undefined) ? '' : String(c).trim();
  return !!s && GRAD_COLOR_RE.test(s);
};

// [2026-06-11 버그수정] 스톱 색이 '완전 투명'인지 — 랜덤 치환 대상 선정에서 제외하기 위한 판별.
//   named transparent / rgba·hsla 4번째 성분 0 / slash 표기 alpha 0 / #rgba·#rrggbbaa alpha 0 을 투명으로 본다.
const isTransparentStopColor = (c) => {
  const s = String(c).trim().toLowerCase();
  if (s === 'transparent') return true;
  const m4 = s.match(/^(rgba|hsla)\(\s*[^,]+,\s*[^,]+,\s*[^,]+,\s*([\d.]+)%?\s*\)$/);
  if (m4 && parseFloat(m4[2]) === 0) return true;
  const ms = s.match(/\/\s*([\d.]+)%?\s*\)$/);
  if (ms && parseFloat(ms[1]) === 0) return true;
  if (s.length === 5 && s.charAt(0) === '#' && s.charAt(4) === '0') return true;
  if (s.length === 9 && s.charAt(0) === '#' && s.substr(7, 2) === '00') return true;
  return false;
};

const normalizeStops = (stops) => {
  if (!Array.isArray(stops)) return null;
  const out = [];
  for (const st of stops) {
    if (!st || typeof st !== 'object') return null;
    if (!isValidGradColor(st.color)) return null;
    const pos = Number(String(st.pos).replace('%', ''));
    if (!Number.isFinite(pos) || pos < 0 || pos > 100) return null;
    out.push({ color: String(st.color).trim(), pos });
  }
  return out.length >= 2 ? out : null;
};

const normalizeAngle = (angle) => {
  const n = Number(angle);
  return (Number.isFinite(n) && n >= 0 && n <= 360) ? n : 180;
};

const buildGradient = (conf) => {
  if (!conf || typeof conf !== 'object') return null;
  const stops = normalizeStops(conf.stops);
  if (!stops) return null;
  const angle = normalizeAngle(conf.angle);
  const randomEnabled = conf.random_enabled === true;
  if (randomEnabled) {
    const palette = Array.isArray(conf.random_palette) ? conf.random_palette.filter(isValidGradColor) : [];
    if (palette.length === 0) return null;
  }
  // [2026-06-11 버그수정] 랜덤 치환 대상 = transparent 가 아닌 '마지막 색 스톱'.
  //   (기존 i===0 치환은 첫 스톱이 transparent 인 구성에서 var 가 투명 스톱에 들어가
  //    완성 문자열에 팔레트색 반영 경로가 없던 버그. 메인 색 스톱을 치환해야 카드별 색이 보인다.)
  let randomIdx = -1;
  if (randomEnabled) {
    for (let i = stops.length - 1; i >= 0; i--) {
      if (!isTransparentStopColor(stops[i].color)) { randomIdx = i; break; }
    }
    if (randomIdx === -1) randomIdx = stops.length - 1; // 전부 투명(이례)이면 마지막 스톱
  }
  // supports 검사용 완성 문자열(var 없음, 원본색) — 비랜덤 모드의 실제 주입 문자열과 동일(중대3).
  const segProbe = stops.map((s) => `${s.color} ${s.pos}%`);
  const valueProbe = `linear-gradient(${angle}deg, ${segProbe.join(', ')})`;
  const supportsOk = (typeof CSS === 'undefined' || typeof CSS.supports !== 'function')
    ? true
    : CSS.supports('background', valueProbe);
  if (!supportsOk) return null;
  // 비랜덤: 완성 문자열 그대로 :root 주입(실측 작동 — 현행 유지)
  if (!randomEnabled) return { value: valueProbe };
  // [2026-06-11 랜덤 재설계] :root 에 정의한 완성 문자열 안의 var(--card-grad-color)는 :root 컨텍스트에서
  //   평가되어 카드별 인라인 색이 무시된다(실측 확정 설계 결함). 랜덤 모드는 완성 문자열 대신
  //   angle/앞스톱들/치환스톱 위치·원본색/뒷스톱들을 분해 변수로 전달하고, var(--card-grad-color) 는
  //   App.css 의 랜덤 전용 ::after 규칙에 직접 둔다(.provider-item 컨텍스트 평가 → 카드별 팔레트색 반영).
  const preStops = stops.slice(0, randomIdx);
  const postStops = stops.slice(randomIdx + 1);
  return {
    random: {
      angle: `${angle}deg`,
      // 앞스톱들: 각 항목 뒤 콤마 포함("a 0%, b 50%,") — App.css 의 빈 fallback var(--…-pre,) 합성 시 문법 성립
      pre: preStops.length ? preStops.map((s) => `${s.color} ${s.pos}%,`).join(' ') : null,
      pos: `${stops[randomIdx].pos}%`,
      fallbackColor: stops[randomIdx].color,
      // 뒷스톱들: 각 항목 앞 콤마 포함(", c 100%")
      post: postStops.length ? postStops.map((s) => `, ${s.color} ${s.pos}%`).join('') : null
    }
  };
};

const applyGradientClasses = (tokensRaw) => {
  if (!tokensRaw) return;
  let tokens = tokensRaw;
  if (typeof tokens === 'string') {
    try { tokens = JSON.parse(tokens); } catch (e) { return; }
  }
  if (typeof document === 'undefined' || !document.body) return;
  const root = document.documentElement;
  const gradient = (tokens && typeof tokens.gradient === 'object') ? tokens.gradient : null;
  ['slot', 'casino'].forEach((kind) => {
    // 비랜덤·랜덤 클래스/변수 전부 제거 후 재적용(설정 해제·모드 전환 시 잔존 차단)
    document.body.classList.remove(`has-gradient-card-overlay-${kind}`);
    document.body.classList.remove(`has-gradient-card-overlay-${kind}-random`);
    root.style.removeProperty(`--gradient-card-overlay-${kind}`);
    ['angle', 'pre', 'pos', 'fallback-color', 'post'].forEach((sfx) => {
      root.style.removeProperty(`--gradient-card-overlay-${kind}-${sfx}`);
    });
    const built = gradient ? buildGradient(gradient[kind]) : null;
    if (!built) return; // 미설정/무효/supports 거부 → 원본 ::after 그대로(무손상)
    if (built.random) {
      // 랜덤: 분해 변수 + 전용 클래스(비랜덤 클래스와 상호 배타) — var 평가는 App.css 랜덤 규칙(::after)에서
      const r = built.random;
      root.style.setProperty(`--gradient-card-overlay-${kind}-angle`, r.angle);
      if (r.pre) root.style.setProperty(`--gradient-card-overlay-${kind}-pre`, r.pre);
      root.style.setProperty(`--gradient-card-overlay-${kind}-pos`, r.pos);
      root.style.setProperty(`--gradient-card-overlay-${kind}-fallback-color`, r.fallbackColor);
      if (r.post) root.style.setProperty(`--gradient-card-overlay-${kind}-post`, r.post);
      document.body.classList.add(`has-gradient-card-overlay-${kind}-random`);
    } else {
      root.style.setProperty(`--gradient-card-overlay-${kind}`, built.value);
      document.body.classList.add(`has-gradient-card-overlay-${kind}`);
    }
  });
};


// [2026-06-11 Phase 0.5] 게임사 카드 높이·한 줄 개수를 데스크톱/태블릿/모바일 3밴드로 분리.
//   WHY: 기존엔 단일값을 데스크톱(@media 1025px↑)에만 적용했다. 태블릿·모바일에서도 따로 설정하려면
//   토큰을 밴드별 객체({desktop,tablet,mobile})로 받아 밴드별 변수·클래스로 주입해야 한다.
//   미래 안전: 과거 단일값(문자/숫자)이 들어오면 desktop 으로 승계한다(데이터 0건이라 사실상 신규지만 규칙 코드화).
//   디자인6 카드는 aspect-ratio:16/9 라 height 명시 시 aspect-ratio 가 무시되고 캐릭터는 object-fit:cover 로 자동 충전된다.
//   숫자 양수 가드는 기존 패턴 유지 — 무효/빈 밴드는 변수·클래스 미주입 → App.css override 비활성 → 원본 반응형 그대로(무손상).
const LAYOUT_BANDS = ['desktop', 'tablet', 'mobile'];

// 밴드별 토큰값을 { desktop, tablet, mobile } 로 정규화. 단일값은 desktop 으로 승계, 나머지 밴드는 빈값.
const normalizeBandToken = (raw) => {
  const out = { desktop: '', tablet: '', mobile: '' };
  if (raw === null || raw === undefined) return out;
  if (typeof raw === 'object') {
    LAYOUT_BANDS.forEach((bp) => {
      const v = raw[bp];
      out[bp] = (v === null || v === undefined) ? '' : String(v).trim();
    });
    return out;
  }
  // 단일 스칼라(과거 스키마) → desktop 승계
  out.desktop = String(raw).trim();
  return out;
};

// 게임사 한 줄 개수(provider_grid_columns) 밴드별 주입.
//   각 밴드값이 '양의 정수'일 때만 --layout-provider-grid-columns-{bp} 변수 + has-layout-provider-grid-columns-{bp} 클래스 부여.
const applyProviderGridColumnsBands = (root, body, raw) => {
  const bands = normalizeBandToken(raw);
  LAYOUT_BANDS.forEach((bp) => {
    body.classList.remove(`has-layout-provider-grid-columns-${bp}`);
    root.style.removeProperty(`--layout-provider-grid-columns-${bp}`);
    const strVal = bands[bp];
    if (!strVal) return;
    // [2026-06-11] 'px'·공백 등 단위/공백을 제거한 뒤 숫자 변환(관리자가 단위 포함 저장해도 호환).
    const n = Number(String(strVal).replace(/px$/i, '').trim());
    if (!Number.isInteger(n) || n <= 0) return; // 잘못된 값 → 미주입(원본 반응형 캡 유지 = 무손상)
    root.style.setProperty(`--layout-provider-grid-columns-${bp}`, String(n));
    body.classList.add(`has-layout-provider-grid-columns-${bp}`);
  });
};

// 게임사 카드 높이(provider_card_height) 밴드별 주입.
//   각 밴드값이 '양수(px)'일 때만 --layout-provider-card-height-{bp}(=Npx) + -num-{bp}(=N) 변수 + has-* 클래스 부여.
//   App.css override 가 밴드별 @media 안에서 카드 height(+디자인6 로고 비율)에 변수를 쓴다.
const applyProviderCardHeightBands = (root, body, raw) => {
  const bands = normalizeBandToken(raw);
  LAYOUT_BANDS.forEach((bp) => {
    body.classList.remove(`has-layout-provider-card-height-${bp}`);
    root.style.removeProperty(`--layout-provider-card-height-${bp}`);
    root.style.removeProperty(`--layout-provider-card-height-num-${bp}`);
    const strVal = bands[bp];
    if (!strVal) return;
    // [2026-06-11] 저장값에 'px'·공백이 붙어도(예: "500px") 단위/공백을 제거한 뒤 숫자 변환.
    //   변수 주입 시 항상 '숫자+px'로 재구성하므로 중복 px("500pxpx") 방지.
    const n = Number(String(strVal).replace(/px$/i, '').trim());
    if (!Number.isFinite(n) || n <= 0) return; // 잘못된 값 → 미주입(원본 카드 크기 유지 = 무손상)
    root.style.setProperty(`--layout-provider-card-height-${bp}`, n + 'px');
    root.style.setProperty(`--layout-provider-card-height-num-${bp}`, String(n));
    body.classList.add(`has-layout-provider-card-height-${bp}`);
  });
};

// [2026-05-20 Phase 4] design_tokens.layout 토큰을 body 클래스로 적용.
//   :root CSS 변수만으론 sidebar 위치·header 스타일 등 레이아웃 변경 불가하므로
//   body.layout-sidebar-position-right 같은 클래스로 토글해 CSS 가 활용한다.
const applyLayoutClasses = (tokensRaw) => {
  // [2026-05-20 Phase 4 Extended] design_tokens.layout 적용.
  //   치수 토큰(header_height·sidebar_width·content_max_width) → :root CSS 변수
  //   boolean 토큰(footer_visible·sidebar_visible) → false 일 때 layout-*-hidden 클래스
  //   enum/string 토큰 → layout-{key}-{value} body 클래스 (기존 동작 유지)
  if (!tokensRaw) return;
  let tokens = tokensRaw;
  if (typeof tokens === 'string') {
    try { tokens = JSON.parse(tokens); } catch (e) { return; }
  }
  const layout = tokens && tokens.layout;
  if (!layout || typeof layout !== 'object') return;
  if (typeof document === 'undefined' || !document.body) return;
  // CSS 변수로 처리할 치수 토큰 키 (body 클래스 아닌 :root 변수로 주입)
  // [2026-06-11 Phase 0.5] provider_grid_columns·provider_card_height 는 CSS_VAR_KEYS 에서 제외하고
  //   밴드별(데스크톱/태블릿/모바일) 전용 헬퍼(applyProvider*Bands)로 처리한다. 객체 토큰을 enum 분기로
  //   잘못 처리(layout-provider-...-[object] 클래스화)하는 것을 막고, 밴드별 변수·클래스로 정확히 주입.
  const CSS_VAR_KEYS = ['header_height', 'sidebar_width', 'content_max_width',
    // [2026-07-30] 메인 배너 높이 — 값이 있으면 CSS 변수(--layout-hero-height)와 활성 클래스를 주입한다.
    'hero_height'];
  // boolean 토큰에서 '_visible' suffix 를 제거해 body 클래스 이름을 단순화한다.
  // footer_visible → footer, sidebar_visible → sidebar → 클래스: layout-footer-hidden
  const BOOLEAN_BASE_MAP = { footer_visible: 'footer', sidebar_visible: 'sidebar' };
  const root = document.documentElement;
  // 기존 layout-* 및 has-layout-* 클래스 일괄 제거 후 재적용
  Array.from(document.body.classList)
    .filter(c => c.startsWith('layout-') || c.startsWith('has-layout-') || c.startsWith('hide-block-') || c === 'has-block-order')
    .forEach(c => document.body.classList.remove(c));
  // [2026-06-11 Phase 0.5] 밴드별 게임사 카드 높이·한 줄 개수 주입(객체 또는 단일값 desktop 승계).
  applyProviderCardHeightBands(root, document.body, layout.provider_card_height);
  applyProviderGridColumnsBands(root, document.body, layout.provider_grid_columns);
  Object.entries(layout).forEach(([k, v]) => {
    if (v === null || v === undefined) return;
    if (k === 'hidden_blocks' || k === 'block_order' || k === 'block_sizes') return; // [2026-06-08] 배열/객체 토큰은 enum 루프에서 제외하고 아래에서 별도 처리(콤마 포함 클래스명 오류 방지)
    // [2026-06-11 Phase 0.5] 밴드별 치수 토큰은 위에서 별도 처리했으므로 일반 루프에서 스킵.
    if (k === 'provider_card_height' || k === 'provider_grid_columns') return;
    const kebab = String(k).replace(/_/g, '-');
    if (CSS_VAR_KEYS.indexOf(k) !== -1) {
      // 치수 토큰: 빈 문자열이면 변수 설정 안 함 (원본 정적 CSS 가 그대로 동작)
      const strVal = String(v).trim();
      if (!strVal) return;
      root.style.setProperty(`--layout-${kebab}`, strVal);
      // has-layout-{key} 클래스로 App.css 오버라이드 selector 활성화
      document.body.classList.add(`has-layout-${kebab}`);
    } else if (typeof v === 'boolean') {
      // boolean 토큰: false → layout-{base}-hidden (App.css 와 명칭 일치)
      //   _visible suffix 를 제거한 base 명을 사용: footer_visible → footer
      if (!v) {
        const base = BOOLEAN_BASE_MAP[k] || kebab;
        document.body.classList.add(`layout-${base}-hidden`);
      }
    } else {
      // enum/string 토큰 → body 클래스 (기존 동작)
      document.body.classList.add(`layout-${kebab}-${v}`);
    }
  });
  // [2026-06-08] 블록 표시/숨김 — layout.hidden_blocks 배열의 블록명을 body 클래스로 추가.
  //   App.css 의 `body.hide-block-{블록} .{블록} { display:none }` 가 해당 블록만 숨긴다.
  //   미설정(기본)은 전부 표시 → 기존 화면 무손상. 관리자에서 끈 블록만 사라진다.
  if (Array.isArray(layout.hidden_blocks)) {
    layout.hidden_blocks.forEach(blk => {
      if (typeof blk === 'string' && /^[a-z0-9-]+$/.test(blk)) {
        document.body.classList.add(`hide-block-${blk}`);
      }
    });
  }
  // [2026-06-08] 블록 순서(위치) — layout.block_order 배열 순서대로 각 블록에 CSS order 부여.
  //   has-block-order 클래스가 있을 때만 App.css 가 .black-home 을 flex+order 로 동작시킨다.
  //   미설정(배열 없음) 시 클래스 미부여 → 기존 배치 그대로(무손상).
  // [2026-07-21 병합 소비] 저장 배열을 그대로 쓰지 않고 '정본 블록 시퀀스'와 병합한다.
  //   WHY: 저장 배열에 없는 블록(예: 이후 추가된 신규게임·추천게임 섹션)은 변수 미주입 → CSS 폴백 order:0
  //   → 순서 저장 도메인에서 페이지 최상단으로 튀는 결함이 있었다(과거 탭 래퍼 order 점프와 동일 계열).
  //   병합 규칙(결정적): ①저장 배열에서 정본에 있는 키만 순서대로 채택(외래·중복 키 무시) ②정본을 순서대로
  //   순회하며 누락 블록을 "작업 중 결과에 이미 존재하는 가장 가까운 정본상 선행 블록" 바로 뒤에 삽입
  //   (연쇄 신규 블록도 정본 인접 순서 유지). 결과: 기존 커스텀 상대 순서 100% 보존 + 신규 블록은
  //   기본(DOM) 위치에 안착 — 향후 어떤 블록을 추가해도 저장 도메인에서 튀지 않는다.
  //   ⚠️ 3중 동기화: 아래 CANONICAL_BLOCK_SEQUENCE 는 (a)본 파일 (b)public/index.html 부트스트랩
  //   (c)admin DesignTokenEditor.jsx 의 ORDER_BLOCKS 와 순서가 반드시 일치해야 한다(= 홈 실제 DOM 순서).
  if (Array.isArray(layout.block_order) && layout.block_order.length) {
    const CANONICAL_BLOCK_SEQUENCE = [
      'black-hero', 'black-categories', 'black-new-games', 'black-recommended-games',
      'black-slots', 'black-casino', 'black-quicklinks', 'black-sports', 'black-events',
      // [2026-07-30] 공지 티커 — 홈 DOM 맨 마지막 구역이라 정본 시퀀스 끝에 둔다.
      //   표시/숨김에서 쓰는 키('notice')와 같은 키를 써서 관리자·화면 기준을 하나로 유지한다.
      'notice'
    ];
    const saved = layout.block_order.filter(
      (blk, idx) => typeof blk === 'string' && /^[a-z0-9-]+$/.test(blk)
        && CANONICAL_BLOCK_SEQUENCE.includes(blk) && layout.block_order.indexOf(blk) === idx
    );
    const merged = [...saved];
    CANONICAL_BLOCK_SEQUENCE.forEach((key, ci) => {
      if (merged.includes(key)) return;
      // [2026-07-30] 공지 티커는 '저장된 순서에 있을 때만' 위치를 갖는다. 저장 전에는 변수를 만들지
      //   않아 CSS 폴백(맨 뒤 고정)이 유지된다 = 순서를 저장하지 않은 도메인의 배치 무변화.
      if (key === 'notice') return;
      let insertAt = 0; // 선행 정본 블록이 결과에 없으면 맨 앞
      for (let p = ci - 1; p >= 0; p--) {
        const prevIdx = merged.indexOf(CANONICAL_BLOCK_SEQUENCE[p]);
        if (prevIdx !== -1) { insertAt = prevIdx + 1; break; }
      }
      merged.splice(insertAt, 0, key);
    });
    document.body.classList.add('has-block-order');
    merged.forEach((blk, i) => {
      root.style.setProperty(`--block-order-${blk}`, String(i));
    });
  }
  // [2026-06-08] 블록 크기(폭) — layout.block_sizes[블록].max_width 를 CSS 변수(--block-w-{블록})로 주입.
  //   App.css 에서 max-width: var(--block-w-{블록}, none) + 좌우 auto 마진으로 폭 제한·중앙정렬.
  //   미설정 시 none → 기존 폭(보통 100%) 유지(무손상).
  if (layout.block_sizes && typeof layout.block_sizes === 'object') {
    Object.entries(layout.block_sizes).forEach(([blk, sz]) => {
      if (!/^[a-z0-9-]+$/.test(blk) || !sz || typeof sz !== 'object') return;
      if (sz.max_width) root.style.setProperty(`--block-w-${blk}`, String(sz.max_width));
      // [2026-07-30] 구역 바탕색 — 값이 있을 때만 주입. 없으면 각 화면의 종전 바탕 유지(무손상).
      if (sz.background) root.style.setProperty(`--block-bg-${blk}`, String(sz.background));
    });
  }
};

const useDomainTheme = () => {
  const [theme, setTheme] = useState(null);
  const [settings, setSettings] = useState({});
  // [2026-05-19] 첫 렌더부터 캐시값 사용 → 로고·디자인 깜빡임 해소.
  const [designTemplate, setDesignTemplate] = useState(getCachedInitial());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadDomainTheme = async () => {
      try {
        // 현재 도메인 추출 (포트 제외)
        let currentDomain = window.location.hostname;
        console.log('현재 도메인:', currentDomain);

        // localhost 처리 - 포트 3006는 u2.nm-001.com으로 매핑
        const isLocalhost = currentDomain === 'localhost' || currentDomain === '127.0.0.1';
        if (isLocalhost && window.location.port === '3006') {
          currentDomain = 'u2.nm-001.com';
          console.log('localhost:3006 detected, using domain:', currentDomain);
        }
        
        // 도메인 기반 디자인 템플릿 정보 가져오기 (캐싱 없이 매번 최신 정보 조회)
        try {
          console.log('도메인 기반 디자인 템플릿 API 호출 시작...');
          console.log('API 호출 도메인:', currentDomain);
          const templateResponse = await api.get(
            `/design-templates/by-domain/${currentDomain}`,
            {
              // 캐시 제어 헤더 (Pragma는 CORS에서 막혀서 제거)
              headers: {
                'Cache-Control': 'no-cache'
              }
            }
          );
          console.log('디자인 템플릿 응답:', templateResponse.data);
          
          if (templateResponse.data.success && templateResponse.data.data) {
            const templateData = templateResponse.data.data;
            setDesignTemplate(templateData);
            // [2026-05-20 Phase 2A] 토큰 즉시 적용 (베이스 사본과 동일 패턴)
            applyDesignTokens(templateData.design_tokens);
            // [2026-05-20 Phase 4] design_tokens.layout 을 body 클래스로 적용
            //   body.layout-sidebar-left·layout-header-fixed·layout-nav-top 형태로 클래스 토글
            applyLayoutClasses(templateData.design_tokens);
            // [2026-06-11 Phase 3~5] 슬롯/카지노 그라데이션 토큰 → has-gradient-card-overlay-slot/-casino 클래스 게이트.
            applyGradientClasses(templateData.design_tokens);
            // [2026-06-11 Phase 5] 렌더 지점이 getActiveDesignTokens() 로 random_palette 를 읽도록 모듈 변수 갱신.
            _activeDesignTokens = parseTokens(templateData.design_tokens);
            // [2026-05-19] 부트스트랩과 동일 키로 캐시 갱신 → 다음 방문 시 즉시 적용.
            saveToCache(templateData);
            console.log('디자인 템플릿 로드 성공:', templateData);
            console.log('템플릿 ID:', templateData.template_id || templateData.id);
            console.log('템플릿 이름:', templateData.design_name);

            // [2026-05-20 Phase 4 Fix] template-{id} 클래스를 classList 로 교체
            //   layout-* 클래스와 공존 가능하도록 document.body.className 전체 덮어쓰기 대신
            //   기존 template-* 클래스만 제거 후 새 template-{id} 추가.
            const templateId = templateData.template_id || templateData.id || 1;
            Array.from(document.body.classList)
              .filter(c => c.startsWith('template-'))
              .forEach(c => document.body.classList.remove(c));
            document.body.classList.add(`template-${templateId}`);
            console.log('Body 클래스 적용 (classList):', document.body.className);

            // 브라우저 타이틀 적용 - 새로운 데이터 구조에서 읽기
            // 먼저 theme.site.title을 확인하고, 없으면 기존 필드 확인
            const browserTitle = templateData.theme?.site?.title ||
                                templateData.user_browser_title ||
                                templateData.site_title;

            if (browserTitle) {
              document.title = browserTitle;
              console.log('브라우저 타이틀 변경:', browserTitle);
            }

            // 파비콘 적용 - 새로운 데이터 구조에서 읽기
            // theme.assets.favicon을 먼저 확인하고, 없으면 기존 필드 확인
            const faviconUrl = templateData.theme?.assets?.favicon ||
                              templateData.user_favicon_url ||
                              templateData.favicon_url;

            if (faviconUrl && faviconUrl !== '/favicon.ico') {
              // 기존 파비콘 링크들 모두 제거
              const existingFavicons = document.querySelectorAll("link[rel*='icon']");
              existingFavicons.forEach(fav => fav.remove());

              // 파비콘 URL 처리
              const finalFaviconUrl = faviconUrl.startsWith('http')
                ? faviconUrl
                : faviconUrl; // 상대 경로 그대로 사용 (Nginx가 프록시 처리)

              // 새 파비콘 추가
              const newFavicon = document.createElement('link');
              newFavicon.rel = 'icon';
              newFavicon.type = 'image/x-icon';
              newFavicon.href = finalFaviconUrl + '?v=' + Date.now(); // 캐시 무효화
              document.head.appendChild(newFavicon);
              console.log('파비콘 적용:', newFavicon.href);

              // shortcut icon도 추가 (일부 브라우저 호환성)
              const shortcutIcon = document.createElement('link');
              shortcutIcon.rel = 'shortcut icon';
              shortcutIcon.type = 'image/x-icon';
              shortcutIcon.href = finalFaviconUrl + '?v=' + Date.now();
              document.head.appendChild(shortcutIcon);
            }

            // 로고 URL 로깅 (디버깅용)
            console.log('메인 로고 URL:', templateData.theme?.assets?.logo || templateData.user_main_logo_url);
            console.log('푸터 로고 URL:', templateData.theme?.assets?.logo || templateData.user_footer_logo_url);
          } else {
            // 템플릿이 없는 경우 기본값으로 진행
            console.warn('도메인에 할당된 템플릿이 없습니다:', currentDomain, '- 기본값 사용');
            // 에러 설정하지 않고 기본값으로 진행
            // setError('이 도메인에는 디자인 템플릿이 할당되지 않았습니다.');
            // setLoading(false);
            // return;
          }
        } catch (templateError) {
          console.error('도메인 기반 템플릿 로드 실패:', templateError);
          console.error('에러 상세:', templateError.response);
          console.error('에러 메시지:', templateError.message);
          console.error('에러 코드:', templateError.code);

          // 404 에러인 경우 에러 상태 설정하지만 계속 진행
          if (templateError.response?.status === 404) {
            const errorMessage = templateError.response?.data?.message || '이 도메인에는 템플릿이 할당되지 않았습니다.';
            console.warn('템플릿 404 에러:', errorMessage, '- 기본값으로 계속 진행');
            // 에러 설정하지 않고 기본값으로 진행
            // setError(errorMessage);
            // setLoading(false);
            // return; // 기본 템플릿 적용하지 않고 종료
          }

          // CORS 에러 또는 네트워크 에러인 경우
          if (templateError.code === 'ERR_NETWORK' || templateError.message.includes('CORS')) {
            console.error('CORS 또는 네트워크 에러 발생');
          }

          // localhost나 기타 에러 시 기본 템플릿 사용
          if (isLocalhost) {
            console.log('localhost 환경에서 기본 템플릿 사용');
          }
        }
      } finally {
        setLoading(false);
      }
    };

    loadDomainTheme();
  }, []);

  // DOM에 테마 적용
  const applyThemeToDOM = (theme) => {
    const root = document.documentElement;
    
    // 색상 적용
    if (theme.colors) {
      root.style.setProperty('--primary-color', theme.colors.primary);
      root.style.setProperty('--secondary-color', theme.colors.secondary);
      root.style.setProperty('--accent-color', theme.colors.accent);
      root.style.setProperty('--background-color', theme.colors.background);
      root.style.setProperty('--text-color', theme.colors.text);
    }
    
    // 폰트 적용
    if (theme.typography) {
      root.style.setProperty('--font-family', theme.typography.fontFamily);
      root.style.setProperty('--font-size-base', `${theme.typography.fontSize}px`);
    }
    
    // [2026-05-20 Phase 4 정리] design_tokens.layout 으로 일원화.
    //   legacy theme.layout 분기에서 추가하던 layout-{type}/sidebar-{pos}/header-{style}
    //   클래스 emission 은 새 layout-{key}-{value} 명명 규칙과 충돌하는 noise 이므로 제거.
    //   토큰 시스템(applyLayoutClasses) 이 일관된 클래스를 부여한다.
    
    // 커스텀 CSS 적용
    if (theme.customCss) {
      const styleElement = document.createElement('style');
      styleElement.textContent = theme.customCss;
      styleElement.id = 'domain-custom-css';
      
      // 기존 커스텀 CSS 제거
      const existingStyle = document.getElementById('domain-custom-css');
      if (existingStyle) {
        existingStyle.remove();
      }
      
      document.head.appendChild(styleElement);
    }
  };

  // 기본 테마 적용
  const applyDefaultTheme = () => {
    const defaultTheme = {
      colors: {
        primary: '#3498db',
        secondary: '#2ecc71',
        accent: '#e74c3c',
        background: '#ffffff',
        text: '#2c3e50'
      },
      typography: {
        fontFamily: 'Noto Sans KR, sans-serif',
        fontSize: 14
      },
      layout: {
        type: 'default',
        sidebarPosition: 'left',
        headerStyle: 'fixed'
      }
    };
    
    setTheme(defaultTheme);
    applyThemeToDOM(defaultTheme);
  };

  return {
    theme,
    settings,
    designTemplate,
    loading,
    error,
    isCustomized: theme && theme.theme_name !== 'default'
  };
};

export default useDomainTheme;